// Firebase Analytics Pinia Store
// Centralized state management for analytics tracking

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { analytics as analyticsUtil, events, eventNameMap } from '@/utils/analytics'
import type { AnalyticsUserProperties, PageViewEventParams } from '@gh/common/models'
import createLogger from '@/utils/logger'
import { getAppPlatform } from '@/utils/pwaTools'
import { syncGtmAdConsent } from '@/utils/gtm'
import { useAuthStore } from './auth'
import { useCustomerStore } from './customer'
import { usePaymentMethodStore } from './paymentMethod'
import { useRemoteConfigStore } from './remoteConfig'

const logger = createLogger('stores/analytics.ts')

interface TrackedEvent {
  eventName: string
  params?: object
  timestamp: number
}

export const useAnalyticsStore = defineStore('analytics', () => {
  // State
  const isEnabled = ref<boolean>(false)
  const debugMode = ref<boolean>(false)
  const userId = ref<string | null>(null)
  const userProperties = ref<Partial<AnalyticsUserProperties>>({})
  const recentEvents = ref<TrackedEvent[]>([])
  const maxRecentEvents = 50 // Keep last 50 events for debugging

  // Track the last identified UID to detect user changes across watchers
  const lastIdentifiedUid = ref<string | null>(null)

  // Computed
  const isAvailable = computed(() => isEnabled.value)

  /**
   * Helper to add an event to recentEvents
   */
  function addToRecentEvents(eventName: string, params?: object) {
    const event: TrackedEvent = {
      eventName,
      params,
      timestamp: Date.now(),
    }

    recentEvents.value.unshift(event)

    // Keep only the most recent events
    if (recentEvents.value.length > maxRecentEvents) {
      recentEvents.value = recentEvents.value.slice(0, maxRecentEvents)
    }
  }

  /**
   * Wrap typed event functions to also record to recentEvents and log on failure
   * Preserves the original function types
   */
  function wrapEventFunction<T extends (...args: never[]) => Promise<void>>(
    originalFn: T,
    eventName: string,
  ): T {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrapped = async (...args: any[]): Promise<void> => {
      try {
        await originalFn(...(args as Parameters<T>))
        // Extract params from args (first argument if exists, otherwise undefined)
        const params = args.length > 0 ? (args[0] as object) : undefined
        addToRecentEvents(eventName, params)
      } catch (error) {
        logger.error(`Analytics event failed: ${eventName}`, error)
      }
    }
    return wrapped as unknown as T
  }

  /**
   * Wrapped events object that preserves types and records to recentEvents
   * Use this instead of the raw events from @/utils/analytics
   * Automatically wraps all functions from the events object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wrappedEvents = {} as any
  for (const functionName in events) {
    const eventName = eventNameMap[functionName]
    if (eventName) {
      wrappedEvents[functionName] = wrapEventFunction(
        events[functionName as keyof typeof events],
        eventName,
      )
    }
  }
  // Type assertion to preserve the original events type structure
  const typedWrappedEvents = wrappedEvents as typeof events

  /**
   * Initialize the Analytics store
   * Checks if Analytics is available and sets the enabled flag
   */
  async function initialize() {
    try {
      const available = await analyticsUtil.isAvailable()
      isEnabled.value = available
      logger.log('Analytics store initialized:', { isEnabled: isEnabled.value })

      // Set up automatic identification when auth or customer data changes
      setupAutomaticIdentification()

      // If a user is already available when Analytics initializes,
      // trigger identification so that Remote Config is fetched
      // only after Analytics user properties have been set.
      const authStore = useAuthStore()
      if (authStore.user) {
        await identifyUser(true).catch((error) => {
          logger.error('Failed to identify user on Analytics initialization:', error)
        })
      }
    } catch (error) {
      logger.error('Failed to initialize Analytics store:', error)
      isEnabled.value = false
    }
  }

  /**
   * Set up watchers to automatically trigger identifyUser when relevant data changes
   */
  function setupAutomaticIdentification() {
    const authStore = useAuthStore()
    const customerStore = useCustomerStore()
    const paymentMethodStore = usePaymentMethodStore()

    // Watch for changes in auth user or roles
    watch(
      [
        () => authStore.user,
        () => authStore.userRole,
        () => paymentMethodStore.subscriptionStatus,
        () => authStore.isTestCustomer,
      ],
      async ([newUser], [oldUser]) => {
        const newUid = newUser?.uid ?? null
        const oldUid = oldUser?.uid ?? null

        // Detect user change: new user logged in (different UID or first login after logout)
        if (newUser && newUid !== lastIdentifiedUid.value) {
          logger.log(
            'Auth user changed (new login or user switch), identifying with force refresh...',
            {
              newUid,
              oldUid,
              lastIdentifiedUid: lastIdentifiedUid.value,
            },
          )
          await identifyUser(true) // Force refresh Remote Config on login/user change
        } else if (!newUser && oldUser) {
          logger.log('User logged out, clearing analytics...')
          lastIdentifiedUid.value = null
          await clearUser()
        } else if (newUser && newUid === lastIdentifiedUid.value) {
          // Same user, but properties changed (role, subscription, etc.)
          logger.log('Auth properties changed for same user, re-identifying...')
          await identifyUser(false) // Don't force refresh for property changes on same user
        }
      },
    )

    // Watch for changes in customer data
    watch(
      () => customerStore.customer,
      async (newCustomer) => {
        if (newCustomer && authStore.user) {
          logger.log('Customer data updated, re-identifying...')
          // If this is the first time we're identifying this user, force refresh
          const currentUid = authStore.user.uid
          const shouldForceRefresh = currentUid !== lastIdentifiedUid.value
          await identifyUser(shouldForceRefresh)
        }
      },
      { deep: true },
    )
  }

  /**
   * Identify the current user and set their properties
   * This should be called on login or when user/customer data is updated
   * @param forceRefreshConfig - Whether to force refresh Remote Config after identification
   */
  async function identifyUser(forceRefreshConfig = false) {
    const authStore = useAuthStore()
    const customerStore = useCustomerStore()
    const paymentMethodStore = usePaymentMethodStore()

    if (!authStore.user) {
      return
    }

    // If customer data is still loading, wait for it before identifying
    // This ensures we have the correct user properties for Remote Config targeting
    if (customerStore.isLoading) {
      logger.log('Customer data still loading, waiting before identification...')
      await customerStore.untilLoadingIsDone()
    }

    if (!authStore.user) {
      return
    }

    const uid = authStore.user.uid
    const appPlatform = getAppPlatform()
    const properties: AnalyticsUserProperties = {
      user_role: authStore.userRole || 'customer',
      is_test_customer: String(authStore.isTestCustomer),
      subscription_status: paymentMethodStore.subscriptionStatus || 'none',
      favourite_classes_count: customerStore.customer?.favouriteClasses?.length || 0,
      has_enrolled: String(!!customerStore.customer?.hasEnrolled),
      state_of_residence: customerStore.customer?.stateOfResidence || 'unknown',
      gender: customerStore.customer?.gender || 'Unknown',
      hear_about_us: customerStore.customer?.hearAboutUs || 'unknown',
      signup_flow_completed: String(!!customerStore.customer?.signupFlowCompleted),
      lead_question_answer: customerStore.customer?.leadQuestionAnswer || 'none',
      app_platform: appPlatform,
    }

    await setUser(uid, properties, forceRefreshConfig)
    // GTM ads only — never copy Firebase user properties into dataLayer.
    syncGtmAdConsent(customerStore.customer?.consents?.privacyPolicy === true)
  }

  /**
   * Set the current user for Analytics, then fetch Remote Config.
   * Order is strict: 1) identify user in Analytics, 2) then initialize Remote Config and pull data.
   * This ensures user-property targeting uses the correct user when fetching config.
   * @param uid - The user's unique ID
   * @param properties - User properties to set (partial - only set the properties you want to update)
   * @param forceRefreshConfig - Whether to force refresh Remote Config after setting properties
   */
  async function setUser(
    uid: string,
    properties: Partial<AnalyticsUserProperties>,
    forceRefreshConfig = false,
  ) {
    try {
      const remoteConfigStore = useRemoteConfigStore()
      const previousUid = userId.value
      const userChanged = previousUid !== uid

      userId.value = uid
      userProperties.value = properties

      // 1) Identify user in Analytics first (debug mode, user ID, user properties)
      const isTestCustomer = properties.is_test_customer === 'true'
      debugMode.value = isTestCustomer
      await analyticsUtil.setDebugMode(isTestCustomer)
      await analyticsUtil.setUserId(uid)
      await analyticsUtil.setUserProperties(properties)

      logger.log('User set in Analytics:', {
        uid,
        properties,
        debugMode: isTestCustomer,
        userChanged,
        previousUid,
        forceRefreshConfig,
      })

      // 2) Only after identification: fetch Remote Config so targeting uses this user
      // Reset Remote Config only when user actually changed (new login or user switch)
      // This ensures personalized config is fetched for the new user
      if (userChanged) {
        logger.log('User changed, resetting Remote Config for new user')
        remoteConfigStore.resetForNewUser()
      }

      // Always fetch config, but force refresh when requested or when user changed
      await remoteConfigStore.fetchConfig(forceRefreshConfig || userChanged)

      // Update last identified UID after successful identification
      if (userChanged) {
        lastIdentifiedUid.value = uid
      }
    } catch (error) {
      logger.error('Failed to set user in Analytics:', error)
    }
  }

  /**
   * Track a custom event (for non-standard events)
   * For standard events, use events from the store (analyticsStore.events) for type safety
   * @param eventName - The name of the event
   * @param params - Optional event parameters
   */
  async function trackEvent(eventName: string, params?: Record<string, unknown>) {
    try {
      await analyticsUtil.trackCustomEvent(eventName, params)
      addToRecentEvents(eventName, params)
    } catch (error) {
      logger.error('Failed to track event:', error)
    }
  }

  /**
   * Clear the current user from Analytics (called on logout).
   * Resets all user-related state and clears Firebase Analytics user ID and debug mode.
   * Call this before resetting Remote Config so the next user gets a clean context.
   */
  async function clearUser() {
    try {
      userId.value = null
      userProperties.value = {}
      debugMode.value = false
      recentEvents.value = []
      lastIdentifiedUid.value = null

      await analyticsUtil.setUserId(null)
      await analyticsUtil.setDebugMode(false)
      syncGtmAdConsent(false)
      const remoteConfigStore = useRemoteConfigStore()
      remoteConfigStore.resetForNewUser()
      logger.log('User cleared from Analytics')
    } catch (error) {
      logger.error('Failed to clear user from Analytics:', error)
    }
  }

  /**
   * Track a page view
   * @param params - Page view parameters
   * @deprecated Use wrappedEvents.trackPageView instead for automatic recentEvents tracking
   */
  async function trackPageView(params: PageViewEventParams) {
    try {
      await wrappedEvents.trackPageView(params)
    } catch (error) {
      logger.error('Failed to track page view:', error)
    }
  }

  /**
   * Clear recent events (for debugging)
   */
  function clearRecentEvents() {
    recentEvents.value = []
  }

  return {
    // State
    isEnabled: computed(() => isEnabled.value),
    debugMode: computed(() => debugMode.value),
    userId: computed(() => userId.value),
    userProperties: computed(() => userProperties.value),
    recentEvents: computed(() => recentEvents.value),
    isAvailable,

    // Typed event functions (wrapped to also record to recentEvents)
    events: typedWrappedEvents,

    // Actions
    initialize,
    identifyUser,
    setUser,
    trackEvent,
    clearUser,
    trackPageView,
    clearRecentEvents,
  }
})
